import { useEffect, useRef } from 'react';

const products = [
  {
    name: 'DURID VPN',
    tagline: 'Privacy Without Compromise',
    description:
      'Military-grade encryption that shields your browsing activity from trackers, advertisers, and malicious actors. Browse the internet freely and securely on any network.',
    features: ['256-bit AES Encryption', 'Zero-Log Architecture', 'Auto-Connect on Unsafe Wi-Fi'],
    image:
      'https://public.readdy.ai/ai/img_res/559c36a5cbfc507f997c8010dc6f850a.jpg',
    accent: 'from-gray-900 to-gray-800',
    textLight: true,
    badge: 'Security',
  },
  {
    name: 'DURID Cleaner',
    tagline: 'Effortless Device Clarity',
    description:
      'Intelligently scans and removes junk files, duplicate photos, and unnecessary app caches — restoring your device\'s speed and freeing up valuable storage space.',
    features: ['Smart Junk Detection', 'Duplicate Photo Finder', 'App Cache Optimizer'],
    image:
      'https://readdy.ai/api/search-image?query=abstract%20minimalist%20iPhone%20app%20interface%20showing%20a%20clean%20sparkling%20iOS%20optimization%20dashboard%20with%20teal%20green%20accent%20icons%20on%20light%20cream%20white%20background%2C%20professional%20product%20mockup%20visualization%2C%20soft%20clean%20aesthetic%2C%20iOS%20design%20language%2C%20subtle%20geometric%20shapes%2C%20no%20text%2C%20premium%20quality%2C%20polished%20render&width=480&height=600&seq=cleaner001&orientation=portrait',
    accent: 'from-stone-50 to-gray-100',
    textLight: false,
    badge: 'Performance',
  },
];

export default function ProductsSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll<HTMLElement>('[data-card]').forEach((el, i) => {
              setTimeout(() => {
                el.style.opacity = '1';
                el.style.transform = 'translateY(0)';
              }, i * 180);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="products" ref={sectionRef} className="py-28 md:py-36 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Heading */}
        <div className="mb-16 md:mb-20">
          <p className="text-xs font-semibold tracking-widest uppercase text-emerald-600 mb-4">
            Our Products
          </p>
          <h2 className="text-4xl md:text-5xl font-thin text-gray-900 leading-tight">
            Built for the <strong className="font-semibold">iOS ecosystem</strong>
          </h2>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {products.map((product, idx) => (
            <div
              key={product.name}
              data-card
              className={`relative rounded-3xl overflow-hidden bg-gradient-to-br ${product.accent} transition-all duration-700 hover:-translate-y-2`}
              style={{ opacity: 0, transform: `translateY(${32 + idx * 10}px)`, transitionDelay: `${idx * 80}ms` }}
            >
              {/* Badge */}
              <div className="absolute top-6 left-6 z-10">
                <span className={`text-xs font-semibold tracking-widest uppercase px-3 py-1.5 rounded-full ${product.textLight ? 'bg-white/10 text-white/70' : 'bg-gray-900/8 text-gray-500'}`}>
                  {product.badge}
                </span>
              </div>

              {/* Image */}
              <div className="w-full h-72 md:h-80 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Content */}
              <div className="p-8 md:p-10">
                <h3 className={`text-2xl font-semibold mb-1 ${product.textLight ? 'text-white' : 'text-gray-900'}`}>
                  {product.name}
                </h3>
                <p className={`text-sm font-medium mb-4 ${product.textLight ? 'text-emerald-400' : 'text-emerald-600'}`}>
                  {product.tagline}
                </p>
                <p className={`text-sm leading-relaxed mb-6 ${product.textLight ? 'text-white/60' : 'text-gray-500'}`}>
                  {product.description}
                </p>
                <ul className="flex flex-col gap-2">
                  {product.features.map((f) => (
                    <li key={f} className={`flex items-center gap-2.5 text-sm ${product.textLight ? 'text-white/70' : 'text-gray-600'}`}>
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-check-line text-emerald-400 text-base" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
